// hooks/reviews/useReviewResponses.ts
import { useCallback, useEffect, useState } from 'react';
import { getAPI, postAPI } from '../../services/api.service';
import { toast } from 'react-toastify';

/**
 * Hook to fetch existing review for assignment+participant, and to save/submit responses.
 *
 * It serializes form answers into a map keyed by `${section}.${index}` (e.g. "pre.0"),
 * and the backend will accept that as `responses`.
 *
 * Usage:
 *  const { existingReview, initialResponses, loading, saving, saveDraft, submitReview, refetch } = useReviewResponses(templateId, assignmentId)
 */
export const useReviewResponses = (templateId?: string, assignmentId?: string,paramParticipantId:string|null="") => {
    const [existingReview, setExistingReview] = useState<any | null>(null);
    const [initialResponses, setInitialResponses] = useState<Record<string, any> | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const participant = JSON.parse(localStorage.getItem('user') || '{}') as { id?: string };
    const participantId = (paramParticipantId && paramParticipantId!="null")?paramParticipantId: participant?.id;

    const fetchReview = useCallback(async () => {
        if (!assignmentId || !participantId) {
            setExistingReview(null);
            setInitialResponses(null);
            return;
        }
        setLoading(true);
        try {
            const res = await getAPI<{ data: any }>('book-review/review-response', { assignmentId, participantId });
            // backend returns shape { status: 200, data: review, ... } from controller above
            const payload = res?.data;
            // payload may be { status:200, data: { ... } }
            const reviewObj = payload?.data ?? payload;
            if (reviewObj) {
                setExistingReview(reviewObj);
                // convert Map-like responses to plain object
                const responsesObj: Record<string, any> = {};
                if (reviewObj.responses) {
                    // sometimes Mongoose Map -> object or Map
                    if (typeof reviewObj.responses === 'object' && !(reviewObj.responses instanceof Map)) {
                        // when responses is plain object
                        Object.assign(responsesObj, reviewObj.responses);
                    } else if (reviewObj.responses instanceof Map) {
                        for (const [k, v] of Object.entries(Object.fromEntries(reviewObj.responses as Map<any, any>))) {
                            responsesObj[k] = v;
                        }
                    }
                }
                setInitialResponses(responsesObj);
            } else {
                setExistingReview(null);
                setInitialResponses(null);
            }
        } catch (err: any) {
            // 404 is fine (no existing review)
            if (err?.response?.status !== 404) {
                console.error('fetch review error', err);
                toast.error(err?.response?.data?.message || 'Failed to fetch existing review (draft).');
            }
            setExistingReview(null);
            setInitialResponses(null);
        } finally {
            setLoading(false);
        }
    }, [assignmentId, participantId]);

    useEffect(() => {
        fetchReview();
    }, [fetchReview]);

    // helper: build responses map from form data. We expect formData.sections.{pre,during,post} arrays
    const buildResponsesFromForm = (formData: any) => {
        const out: Record<string, any> = {};
        if (!formData || !formData.sections) return out;
        (['pre', 'during', 'post'] as const).forEach((sec) => {
            const arr = formData.sections?.[sec] ?? [];
            arr.forEach((item: any, idx: number) => {
                // if form item is object { value: ... } (as in your form) or primitive
                const val = (item && typeof item === 'object' && 'value' in item) ? item.value : item;
                out[`${sec}.${idx}`] = val;
            });
        });
        return out;
    };

    // Save or update draft (status = 'draft')
    const saveDraft = async (formData: any) => {
        if (!assignmentId || !templateId || !participantId) {
            toast.error('Missing assignment, template or user info. Cannot save draft.');
            return null;
        }
        setSaving(true);
        try {
            const responses = buildResponsesFromForm(formData);
            const body = {
                assignmentId,
                templateId,
                participantId,
                responses,
                status: 'draft'
            };
            const res = await postAPI('book-review/review-response', body);
            const created = res?.data ?? res;
            toast.success('Draft saved');
            // refresh existing
            await fetchReview();
            return created;
        } catch (err: any) {
            console.error('saveDraft error', err);
            toast.error(err?.response?.data?.message || 'Failed to save draft');
            return null;
        } finally {
            setSaving(false);
        }
    };

    // Submit final responses (status = 'submitted')
    const submitReview = async (formData: any) => {
        if (!assignmentId || !templateId || !participantId) {
            toast.error('Missing assignment, template or user info. Cannot submit.');
            return null;
        }
        setSaving(true);
        try {
            const responses = buildResponsesFromForm(formData);
            const body: any = {
                assignmentId,
                templateId,
                participantId,
                responses,
                status: 'submitted',
                submittedAt: new Date().toISOString()
            };
            const res = await postAPI('book-review/review-response', body);
            const created = res?.data ?? res;
            if (res.status !== 200 && res.status !== 201) {
                toast.error('Failed to submit responses');
                return null;
            } else
                toast.success('Responses submitted');
            await fetchReview();
            return created;
        } catch (err: any) {
            console.error('submitReview error', err);
            toast.error(err?.response?.data?.message || 'Failed to submit responses');
            return null;
        } finally {
            setSaving(false);
        }
    };

    return {
        existingReview,
        initialResponses, // object keyed by "pre.0" etc. (or null)
        loading,
        saving,
        saveDraft,
        submitReview,
        refetch: fetchReview
    };
};
